/**
 * Host-side half of the input-to-photon measurement.
 *
 * The host contributes no timestamps — only the correlation key. It remembers which
 * sampled input it most recently forwarded for injection, and attaches that sequence
 * number to the next frame its encoder produces. See `inputPhotonContracts.ts`.
 *
 * "Next frame after injection" is the tightest bound available here: the host cannot know
 * which frame first shows the game's response without inspecting pixels, but it does know
 * that no earlier frame can possibly contain it.
 */

import {
  INPUT_PHOTON_MESSAGE_TYPE,
  type InputPhotonTag,
} from './inputPhotonContracts';

/** Lifetime counts per peer: how many sampled inputs arrived, how many frames were tagged. */
export type HostInputPhotonCounts = { noted: number; tagged: number };

export class HostInputPhotonTagger {
  /** peerId → sequence number awaiting a frame. At most one in flight per peer. */
  private readonly pendingByPeer = new Map<string, number>();
  private readonly countsByPeer = new Map<string, HostInputPhotonCounts>();

  /**
   * Record that a sampled input was forwarded for injection.
   *
   * A newer input replaces an older un-tagged one rather than queueing. Only one
   * measurement is in flight per peer by design, and the freshest input is the one whose
   * frame is about to be encoded.
   */
  noteInputForwarded(peerId: string, inputSeq: number): void {
    if (!Number.isFinite(inputSeq)) return;
    this.pendingByPeer.set(peerId, inputSeq);
    this.counts(peerId).noted += 1;
  }

  /** How far the host half of the chain has got for a peer; zeros before any input. */
  countsFor(peerId: string): HostInputPhotonCounts {
    return { ...this.counts(peerId) };
  }

  private counts(peerId: string): HostInputPhotonCounts {
    let counts = this.countsByPeer.get(peerId);
    if (!counts) {
      counts = { noted: 0, tagged: 0 };
      this.countsByPeer.set(peerId, counts);
    }
    return counts;
  }

  /**
   * Claim the pending input for a freshly encoded frame.
   *
   * Returns the tag to send to the guest, or null when no sampled input is waiting — which
   * is the common case, since frames vastly outnumber sampled inputs.
   */
  tagFrame(peerId: string, rtpTimestamp: number): InputPhotonTag | null {
    const inputSeq = this.pendingByPeer.get(peerId);
    if (inputSeq === undefined) return null;
    if (!Number.isFinite(rtpTimestamp)) return null;
    this.pendingByPeer.delete(peerId);
    this.counts(peerId).tagged += 1;
    return {
      type: INPUT_PHOTON_MESSAGE_TYPE,
      role: 'tag',
      inputSeq,
      rtpTimestamp,
    };
  }

  clearPeer(peerId: string): void {
    this.pendingByPeer.delete(peerId);
    this.countsByPeer.delete(peerId);
  }

  clearAll(): void {
    this.pendingByPeer.clear();
    this.countsByPeer.clear();
  }
}
